import { createColumnHelper } from '@tanstack/react-table';
import { HeadCell, SortableHeadCell, TextCell } from 'components/tables/cells';
import { toDefaultDateFormatOrNotAvailable } from 'utils/dateUtils';
import {
	NumberColumnProps,
	TextColumnProps,
} from 'components/tables/columnTypes';
import { ProductIcon } from 'components/common/Product/ProductIcon/ProductIcon';
import { HStack, Text } from '@montel/montelpro-shared-components-v2';
import { getProductConfig } from 'domain/product/productMap';
import { getSubscriptionStatusColor } from 'domain/subscription/interfaces/Subscription';
import { SubscriptionBundle } from 'domain/subscription/interfaces/SubscriptionBundle';

const columnHelper = createColumnHelper<SubscriptionBundle>();

export const bundleTableColumns = {
	product: (overrideProps?: TextColumnProps<SubscriptionBundle>) =>
		columnHelper.accessor((row) => row.productId, {
			id: 'product',
			enableSorting: true,
			header: ({ header }) => (
				<HeadCell
					text="Product"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => {
				const product = getProductConfig(info.getValue());
				return (
					<HStack align="center" wrap="nowrap">
						{product?.id && <ProductIcon productId={product.id} />}
						<Text size="sm">
							{product?.name ?? info.getValue()}
						</Text>
					</HStack>
				);
			},
			...overrideProps?.columnDefProps,
		}),
	type: (overrideProps?: TextColumnProps<SubscriptionBundle>) =>
		columnHelper.accessor((row) => row.type, {
			id: 'type',
			enableSorting: true,
			header: ({ header }) => (
				<SortableHeadCell
					text="Type"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<TextCell
					text={info.getValue()}
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),
	status: (overrideProps?: TextColumnProps<SubscriptionBundle>) =>
		columnHelper.accessor((row) => row.status, {
			id: 'status',
			enableSorting: true,
			header: ({ header }) => (
				<SortableHeadCell
					text="Status"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<TextCell
					text={info.getValue()}
					color={getSubscriptionStatusColor(info.getValue())}
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),
	startDate: (overrideProps?: TextColumnProps<SubscriptionBundle>) =>
		columnHelper.accessor((row) => row.startDate, {
			id: 'startDate',
			enableSorting: true,
			header: ({ header }) => (
				<SortableHeadCell
					text="Start Date"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<TextCell
					text={toDefaultDateFormatOrNotAvailable(
						info.getValue(),
						'N/A',
						'DD MMM YYYY',
					)}
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),
	endDate: (overrideProps?: TextColumnProps<SubscriptionBundle>) =>
		columnHelper.accessor((row) => row.endDate, {
			id: 'endDate',
			enableSorting: true,
			header: ({ header }) => (
				<SortableHeadCell
					text="End Date"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<TextCell
					text={toDefaultDateFormatOrNotAvailable(
						info.getValue(),
						'N/A',
						'DD MMM YYYY',
					)}
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),
	seats: (overrideProps?: NumberColumnProps<SubscriptionBundle>) =>
		columnHelper.accessor((row) => row.seats, {
			id: 'seats',
			enableSorting: true,
			header: ({ header }) => (
				<SortableHeadCell
					text="Seats"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<TextCell
					text={`${
						info.row.original.occupiedSeats
					}/${info.getValue()}`}
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),
	description: (overrideProps?: TextColumnProps<SubscriptionBundle>) =>
		columnHelper.accessor((row) => row.description, {
			id: 'description',
			enableSorting: false,
			header: ({ header }) => (
				<HeadCell
					text="Description"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<TextCell
					text={info.getValue() ?? ''}
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),
};
